/**
 * Dev-only URL switches.
 *
 * Until the LevelMap and ProfileSelect scenes exist (Prompt 7), `?level=N` is the
 * only way into the Game scene, so Preload honours it as a direct route. These
 * are inert in a production build.
 */
import { autoplayOptionsFrom } from './autoplay';
import type { AutoplayOptions } from './autoplay';
import type { AnimalArtStyle } from '../data/animals';

function searchParams(): URLSearchParams | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search);
}

/** `?level=30` -> 30. Null when absent, malformed, or in a production build. */
export function devLevelFromQuery(): number | null {
  const params = searchParams();
  const raw = params?.get('level');
  if (raw === null || raw === undefined) return null;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : null;
}

/** `?animal=orca` -> 'orca', for eyeballing a different animal before Prompt 7. */
export function devAnimalFromQuery(): string | null {
  const params = searchParams();
  const raw = params?.get('animal');
  return raw === null || raw === undefined || raw === '' ? null : raw;
}

/**
 * Dev-only state published to window.__questJump, so the Playwright harness can
 * wait for the game rather than guessing at animation timings. Prompt 13's
 * autoplay mode reads the same object. Compiled out of production builds.
 */
export interface DevState {
  scene?: string;
  platform?: number;
  platformCount?: number;
  busy?: boolean;
  word?: string;
  typed?: string;
  duplicateKeys?: number;
  finished?: boolean;
  paused?: boolean;
  level?: number;
  /** Tide Run: levels cleared so far this run; -1 outside a run. */
  tideCleared?: number;
  /** Tide Run: the water has the animal. */
  caught?: boolean;
  /** Tide Run summary: how the run ended. */
  outcome?: string;
}

export function setDevState(partial: DevState): void {
  if (!import.meta.env.DEV) return;
  if (typeof window === 'undefined') return;
  const holder = window as unknown as { __questJump?: DevState };
  holder.__questJump = { ...(holder.__questJump ?? {}), ...partial };
}

/** `?scene=LevelMap` boots straight into one scene, for screenshots. */
export function devSceneFromQuery(): string | null {
  const raw = searchParams()?.get('scene');
  return raw === null || raw === undefined || raw === '' ? null : raw;
}

/** `?results=1` boots the Results scene with a sample run, to review its layout. */
export function devResultsRequested(): boolean {
  return searchParams()?.get('results') === '1';
}

/** `?unlock=adelie-penguin` shows Results as it looks when an animal unlocks. */
export function devUnlockFromQuery(): string {
  const raw = searchParams()?.get('unlock');
  return raw === null || raw === undefined ? '' : raw;
}

/**
 * `?gate=open` skips the grown-up maths question on ParentStats.
 *
 * Dev builds only (the caller checks import.meta.env.DEV), so the screenshot
 * harness can review the stats screen without solving a random sum.
 */
export function devGateOpen(): boolean {
  return searchParams()?.get('gate') === 'open';
}

/** `?wordlist=on` shows the word list in a dev level, which has no profile to read it from. */
export function devWordListOn(): boolean {
  return searchParams()?.get('wordlist') === 'on';
}

/** `?autoplay=1` (plus wpm, typos, misses, seed) types the level by itself. */
export function devAutoplayOptions(): AutoplayOptions | null {
  const params = searchParams();
  return params === null ? null : autoplayOptionsFrom(params);
}

/** `?style=classic` brings back the original animal art, for comparison. */
export function devArtStyle(): AnimalArtStyle {
  return searchParams()?.get('style') === 'classic' ? 'classic' : 'cute';
}

/** `?tide=1` starts a Tide Run (at `?level=N`, else level 1). */
export function devTideRequested(): boolean {
  return searchParams()?.get('tide') === '1';
}

/** `?hints=on` shows the keyboard hints in a dev level. */
export function devHintsOn(): boolean {
  return searchParams()?.get('hints') === 'on';
}

/** `?stars=2` picks how many stars the sample run earned. */
export function devSampleStars(): number {
  const raw = searchParams()?.get('stars');
  const value = raw === null || raw === undefined ? 3 : Number.parseInt(raw, 10);
  return Number.isFinite(value) ? Math.min(3, Math.max(0, value)) : 3;
}

/** `?wordseed=7` fixes a level's words, to replay it exactly. Null draws new ones. */
export function devWordSeed(): number | null {
  const raw = searchParams()?.get('wordseed');
  const value = raw === null || raw === undefined ? Number.NaN : Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : null;
}
